package org.multiplatform.kotfolio.pages.compose.side_effect

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/side_effect/remember")
@Composable
fun RememberPage() {
    BlogLayout(
        PageProperties(
            "remember",
            "Local state you want to keep while the composable stays in the composition."
        ),
        BlogProperties("Jetpack Compose", "remember"))
    {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "remember - (Short-Lived UI State - Function memoization - Kobweb Scroll)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Storage Location: Composition Slot Table",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember keeps the state inside a composable and avoids resetting it on every recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember keeps state only during recompositions. If the activity rotates → the value resets.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember is similar to var or val in Kotlin, but with an important difference the value of a remember variable is retained across recompositions, allowing the value to persist without resetting every time the composable function is recomposed. This means that the value of the variable is preserved even if the Composable function is re-executed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Recomposition happens when the inputs to a composable function change, causing it to be re-executed. remember ensures that the value is not recalculated or re-initialized during recompositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you use remember, Compose caches the value during the initial composition. During recomposition, Compose checks the cache and reuses the stored value instead of recalculating or reinitializing it. However, if the composable leaves the composition (like when navigating away from a screen), the value is cleared.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember{...} It is an important API in Compose, which is used to wrap some logic with high computational cost or some state data, so as to avoid repeated execution with functions. Remember stores values in the Composition, and will forget them if the composable that called remember is removed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In composable, calculations can run frequently. Use remember to store the results of expensive calculations. This ensures the calculation runs just only once, and you can access the stored result whenever needed. This avoids redundant calculations on every Recomposition. Recomposition is the process of updating the UI based on changes to the underlying data or state. When the state of a composable function changes, Compose automatically recomposes the UI to reflect the new state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Recomposition is efficient because Compose only updates the parts of the UI that have changed. The most common use case is creating state variables using remember { mutableStateOf(initialValue) }. This allows the composable to track and update its internal state that can affect the UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The value of a remember variable can be changed using a lambda function that is passed to it. Whenever the value of a remember variable changes, the Composable function that uses it will be recomposed with the new value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A value computed by remember will be stored in the composition tree, and only be recomputed if the keys to remember the change.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The value stored by remember is lost when the composable leaves the composition. This means if the composable is removed from the UI and then added back, the value will be reset. Also, remember does not survive configuration changes like screen rotations or process death.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember stores objects in the Composition, and forgets the object when the composable that called remember is removed from the Composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Local to Composable Function",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The value stored using remember is only accessible within the same composable function or its child composable. It is not shared across various composable functions.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Lifetime Management",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The value stored with remember remains available as long as the composable function or its parent composable is alive in the composition hierarchy. When the composable is removed from the hierarchy, the stored value is also garbage collected.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }
            }

            //https://github.com/roywasker/Portfolio.git
            KotlinCode(
                code = """
                    @Composable
                    fun BackToTopButton() {
                        var scroll: Double? by remember { mutableStateOf(null) }

                        LaunchedEffect(Unit) {
                            window.addEventListener(type = "scroll", callback = {
                                scroll = document.documentElement?.scrollTop
                            })
                        }
                    }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    
                    @Composable
                    fun CompactSlider(
                        value: Float,
                        min: Float,
                        max: Float,
                        onValueChange: (Float) -> Unit,
                        modifier: Modifier = Modifier,
                        activeColor: Color = AppColors.Cream,
                        inactiveColor: Color = AppColors.MetalDarkBorder,
                        thumbColor: Color = AppColors.Cream
                    ) {
                        val range = (max - min).coerceAtLeast(1f)
                        var fraction by remember(value, min, max) {
                            mutableFloatStateOf(((value - min) / range).coerceIn(0f, 1f))
                        }

                        Canvas(
                            modifier = modifier
                                .height(16.dp)
                                .fillMaxWidth()
                                .pointerInput(min, max) {
                                    detectHorizontalDragGestures(
                                        onDragStart = { offset ->
                                            fraction = (offset.x / size.width).coerceIn(0f, 1f)
                                            onValueChange(min + fraction * range)
                                        },
                                        onHorizontalDrag = { change, _ ->
                                            change.consume()
                                            fraction = (change.position.x / size.width).coerceIn(0f, 1f)
                                            onValueChange(min + fraction * range)
                                        }
                                    )
                                }
                        ) {
                            val trackHeight = 4.dp.toPx()
                            val thumbRadius = 6.dp.toPx()
                            val trackY = (size.height - trackHeight) / 2
                            val fillWidth = size.width * fraction

                            drawRoundRect(
                                color = inactiveColor,
                                topLeft = Offset(0f, trackY),
                                size = Size(size.width, trackHeight),
                                cornerRadius = CornerRadius(trackHeight / 2)
                            )

                            if (fillWidth > 0) {
                                drawRoundRect(
                                    color = activeColor,
                                    topLeft = Offset(0f, trackY),
                                    size = Size(fillWidth, trackHeight),
                                    cornerRadius = CornerRadius(trackHeight / 2)
                                )
                            }

                            val thumbX = fillWidth.coerceIn(thumbRadius, size.width - thumbRadius)
                            drawCircle(
                                color = thumbColor,
                                radius = thumbRadius,
                                center = Offset(thumbX, size.height / 2)
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    // ❌ JUNIOR: remember WITHOUT key — value never updates
                    @Composable
                    fun TransferRow(transfer: Transfer) {
                        val formattedDate = remember {
                            SimpleDateFormat("MMM dd, yyyy", Locale.getDefault()).format(transfer.date)
                        }
                        // If `transfer` changes (different item in a LazyColumn), formattedDate shows OLD date!
                        // remember {} caches the FIRST value and never recomputes


                    Text(formattedDate)  // Shows stale data
                    }
                    
                    // ✅ SENIOR: remember(key) - recomputes when key changes
                    @Composable
                    fun TransferRow(transfer: Transfer) {
                        val formattedDate = remember(transfer.date) {
                            SimpleDateFormat("MMM dd, yyyy", Locale.getDefault()).format(transfer.date)
                        }
                        // Recomputes ONLY when transfer.date changes
                        // In a LazyColumn: each item has a different transfer.date → correct formatting
                        val formattedAmount = remember(transfer.amount, transfer.currency) {
                            NumberFormat.getCurrencyInstance(Locale.getDefault()).apply {
                                currency = Currency.getInstance(transfer.currency)
                            }.format(transfer.amount)
                        }
                        // Two keys: recomputes when EITHER amount or currency changes
                        Text("$formattedDate - $formattedAmount")
                    }
                    
                    // RULE: If the remembered value depends on ANYTHING that can change,
                    // pass that thing as a key to remember().
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    actual fun PlayerDataStep(
                        initialFirstName: String
                    ) {
                        var firstName by remember(initialFirstName) { mutableStateOf(initialFirstName) }
                    } 
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    val list by remember { mutableState0f(value = listOf(1, 2, 3)) }
                    
                    //Function memoization
                    //Introduced by String Skipping Mode, used by default since Kotlin 2.0.20 modern standard
                    val provider = remember(key1= list) { { list } }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    sealed class IconPlacement {
                        data object Start : IconPlacement()
                        data object End : IconPlacement()

                        fun isStart(): Boolean {
                            return this is Start
                        }
                    }

                    var searchIconPlacement: IconPlacement by remember { mutableStateOf(IconPlacement.Start) }
                    """.trimIndent()
            )

            //https://github.com/Kevn-Block/JuliaSets-KMP
            KotlinCode(
                code = """
                    @Composable
                    private fun NumberEditor(
                        initialNumber: String,
                        onNumberChanged: (Int) -> Unit,
                        onError: (String?) -> Unit
                    ) {
                        var textInput by remember(initialNumber) { mutableStateOf(initialNumber) }

                        TextField(
                            modifier = Modifier
                                .border(1.dp, Color(0xFFBDBDBD), RoundedCornerShape(4.dp))
                                .padding(4.dp),
                            value = textInput,
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                            singleLine = true,
                            onValueChange = {
                                textInput = it
                                val parsedInput = it.toIntOrNull()
                                val error = errorFromNumber(it)
                                if (error == null && parsedInput != null) {
                                    onNumberChanged(parsedInput)
                                }
                                onError(error)
                            },
                        ) {
                            TextInput(
                                placeholder = { Text("") }
                            )
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Recomposition vs Configuration change - Mental model",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Recomposition - ✅ survives",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Screen rotation - ❌ lost",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Process death - ❌ lost",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Not store remember",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Not",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Long-lived data",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Business logic",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Network / DB state",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "ViewModel",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "StateFlow",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "remember { derivedStateOf { } } Derived values cached with remember",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "When to use",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Anything that should not reset during recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Any local state you want to keep while the composable stays in the composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How remember Works in Compose",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When Compose executes a function with remember, it checks if it has already stored a value for that key in the Composition (which is like an internal tree storing UI elements and states). If the value exists, it returns it; otherwise, it stores the new value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "First Composition: When the composable first runs, the remember lambda block executes once to create the initial mutableStateOf(0) state object.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "State Update: When we update, we’re modifying the existing state object, not creating a new one.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Recomposition: Changing the state triggers a recomposition of the composable function.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "During Recomposition: The composable executes again, but the remember function returns the previously created state object without re-executing its lambda block.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "The remember API: Surface and overloads",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember overload takes no keys. It calls currentComposer.cache(false, calculation), passing false to indicate the cached value is never invalidated by key changes:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    inline fun <T> remember(
                        crossinline calculation: @DisallowComposableCalls () -> T
                    ): T = currentComposer.cache(false, calculation)
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The single key variant passes the result of currentComposer.changed(key1) as the invalid flag. If the key changed since the last composition, the cached value is recalculated:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    inline fun <T> remember(
                        key1: Any?,
                        crossinline calculation: @DisallowComposableCalls () -> T,
                    ): T {
                        return currentComposer.cache(
                            currentComposer.changed(key1), calculation
                        )
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The two key variant combines checks with or:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    inline fun <T> remember(
                        key1: Any?,
                        key2: Any?,
                        crossinline calculation: @DisallowComposableCalls () -> T,
                    ): T {
                        return currentComposer.cache(
                            currentComposer.changed(key1) or
                                currentComposer.changed(key2),
                            calculation,
                        )
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Notice the use of or instead of ||. This is not a stylistic choice. Each call to changed() reads the next slot from the slot table and advances the reader cursor forward. If || were used and the first changed() returned true, the second changed() would be short circuited and never execute. The cursor would not advance past the second key's slot, and every subsequent slot read in the composable would be off by one. The non short circuiting or ensures every changed() call executes, keeping the cursor synchronized.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The vararg overload follows the same pattern, iterating over all keys with or:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The @DisallowComposableCalls annotation on the lambda prevents composable function calls inside the remember block. This is enforced because remember's lambda executes outside the normal composition flow, during cache evaluation, where the Composer is not prepared for nested composable calls.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    inline fun <T> remember(
                        vararg keys: Any?,
                        crossinline calculation: @DisallowComposableCalls () -> T,
                    ): T {
                        var invalid = false
                        for (key in keys) invalid = invalid or currentComposer.changed(key)
                        return currentComposer.cache(invalid, calculation)
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you write a composable function that uses remember, the Compose compiler plugin transforms it. Consider this source code:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "After transformation, the function receives additional parameters and is wrapped in group bookkeeping. The transformed output looks approximately like this (simplified):",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    @Composable
                    fun Greeting(name: String) {
                        val message = remember { "Hello, $name" }
                        Text(message)
                    }
                    
                    fun Greeting(name: String, $composer: Composer, $changed: Int) {
                        $composer.startRestartGroup(0x7a3d2f1e)

                        val message = $composer.cache(false) { "Hello, $name" }
                        Text(message, $composer, 0)

                        $composer.endRestartGroup()?.updateScope { c, _ ->
                            Greeting(name, c, $changed or 0b1)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //currentComposer represents the current composition.
                    //remember(inputs, calculation) checks if the value already exists in the composition.
                    //If it does, it returns the existing value; otherwise, it stores the new one.

                    @Composable
                    inline fun <T> remember(vararg inputs: Any?, calculation: () -> T): T {
                        val scope = currentComposer
                        return scope.remember(inputs, calculation)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                   //remember with a Lambda 
                   @Composable
                   fun RememberLambdaExample() {
                       val greet = remember { 
                               // lambda function
                               { name: String -> "Hello, $name!" } 
                       }

                       Text(greet("User"))
                   }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun ExpensiveCalculationScreen() {
                      val result by remember(initialValue = { calculateSomethingExpensive() }) {
                        // ...
                      }
                      Text(text = result)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun ExpandingCard(title: String, body: String) {
                        // expanded is "internal state" for ExpandingCard
                        var expanded by remember { mutableStateOf(false) }

                        Card {
                            Column() {
                                Text(text = title)

                                // content of the card depends on the current value of expanded
                                if (expanded) {
                                    // TODO: show body & collapse icon
                                } else {
                                    // TODO: show expand icon
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Map using search/filter 
                    
                    object DataProvider {
                        fun getItemList(): Map<String, List<Item>> {
                            return mapOf(
                                "Fresh Fruits" to listOf(
                                    Item(1, "Apple", "A sweet, crisp red fruit, perfect for a healthy snack.", R.drawable.apple),
                                  ),
                                "Farm Vegetables" to listOf(
                                    Item(2, "Carrot", "A long, orange root vegetable, great for eyesight.", R.drawable.carrot),
                                )
                            )
                        }
                    }
                    
                    val allItems = remember { DataProvider.getItemList() }
                    
                    var searchQuery by remember { mutableStateOf("") }
                    
                    val filteredItems = remember(searchQuery, allItems) {
                        if (searchQuery.isBlank()) {
                            allItems
                        } else {
                            allItems.mapValues { (_, itemList) ->
                                itemList.filter { it.name.contains(searchQuery, ignoreCase = true) }
                            }.filter { it.value.isNotEmpty() }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //debounce using search/filter
                    var localQuery by remember { mutableStateOf("") }
                    
                    LaunchedEffect(localQuery) {
                        snapshotFlow { localQuery }
                        .debounce(400)
                        .collect { debouncedText ->
                            //viewmodel
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/gcaguilar/biciradar.git
            KotlinCode(
                code = """
                     //Multiple key using search/filter
                     
                     @Composable
                     private fun CitySelectionScreen(onCitySelected: (City) -> Unit) {
                       var searchQuery by remember { mutableStateOf("") }
                       val sortedCities = remember { City.entries.sortedBy { it.displayName } }
                       val normalizedQuery =
                         remember(searchQuery) { searchQuery.trim().normalizedForSearch() }
                      
                       val filteredCities =
                         remember(normalizedQuery, sortedCities) {
                           if (normalizedQuery.isBlank()) {
                             sortedCities
                           } else {
                             sortedCities.filter { city ->
                               city.displayName.normalizedForSearch().contains(normalizedQuery)
                             }
                           }
                         }
                     }
                    """.trimIndent()
            )



            KotlinCode(
                code = """
                    //Multiple composable

                    @Composable
                    fun ProfileScreen(userId: String) {
                      var user by remember(initialValue = { User() }) {
                        mutableStateOf(User())
                      }

                      fetchData(userId) { fetchedUser ->
                        user = fetchedUser
                      }

                      Text(text = user.name)

                      @Composable
                      fun UserDetails(user: User) {
                        Text(text = user.name)
                      }

                      UserDetails(user)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun UserSettingsScreen(viewModel: UserSettingsViewModel) {

                        val userName by remember { derivedStateOf { viewModel.userName.value } }

                        TextField(
                            value = userName,
                            onValueChange = { viewModel.onUserNameChange(it) }
                        )

                        // Other user settings TextFields
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.LaunchedEffect
                    import androidx.compose.runtime.getValue
                    import androidx.compose.runtime.mutableStateOf
                    import androidx.compose.runtime.remember
                    import androidx.compose.runtime.setValue
                    import kotlinx.coroutines.delay
                    import kotlin.time.Duration.Companion.seconds

                    data class TimerState(
                      val minutes: Int = 1,
                      val seconds: Int = 0,
                    )

                    @Composable
                    fun TimerDomain(seed: TimerState = TimerState()): TimerState {
                      var state: TimerState by remember { mutableStateOf(seed) }

                      LaunchedEffect(Unit) {
                        while (true) {
                          delay(1.seconds)

                          state = when {
                            state.minutes > 0 -> state.copy(
                              minutes = state.minutes - 1,
                              seconds = 59
                            )

                            state.seconds > 0 -> state.copy(
                              seconds = state.seconds - 1
                            )

                            else -> break
                          }
                        }
                      }

                      return state
                    }

                    import app.cash.molecule.RecompositionMode
                    import app.cash.molecule.moleculeFlow
                    import app.cash.turbine.test
                    import io.github.xxfast.counter.screens.timer.TimerDomain
                    import io.github.xxfast.counter.screens.timer.TimerState
                    import kotlinx.coroutines.test.runTest
                    import kotlin.test.Test
                    import kotlin.test.assertEquals

                    class TimerTest {
                      @Test
                      fun `Test minute rollover`() = runTest {
                        moleculeFlow(RecompositionMode.Immediate) { TimerDomain() }
                          .test {
                            assertEquals(
                              expected = TimerState(1, 0),
                              actual = awaitItem()
                            )

                            assertEquals(
                              expected = TimerState(0, 59),
                              actual = awaitItem()
                            )
                          }
                      }

                      @Test
                      fun `Test timer stops at zero`() = runTest {
                        moleculeFlow(RecompositionMode.Immediate) { TimerDomain() }
                          .test {
                            repeat(60) { this.awaitItem() }
                            assertEquals(
                              expected = TimerState(0, 0),
                              actual = awaitItem()
                            )
                          }
                      }
                    }
                    """.trimIndent()
            )

            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "The Molecule library is the idea that you can use Compose solely as a mechanism of producing state values and not as something which does rendering. Using Molecule allows us to transform a composable method into a Flow or StateFlow with Kotlin Multiplatform to use the Compose runtime on non-Compose platforms. Molecule also enables the presenter to return an initial state value, which the Compose UI on Android requires. Writing Composable functions, in turn, allows us to write simplified business logic within our presenters, as we no longer need to use the Flow operators such as map and flatmap to return the state in a Flow or StateFlow.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = $$"""
                    //molecule library

                    //StateFlow
                    val count: StateFlow<Int> = scope.launchMolecule {
                      Counter(1, 10)
                    }

                    //1
                    fun CoroutineScope.launchCounter(start: Int = 0): StateFlow<Int> {
                      return launchMolecule(mode = ContextClock) {
                        counter(start)
                      }
                    }

                    @Composable fun counter(start: Int = 0): Int {
                      var count by remember { mutableStateOf(start) }

                      LaunchedEffect(Unit) {
                        while (true) {
                          delay(1.seconds)
                          count++
                        }
                      }

                      return count
                    }

                    @Composable fun CounterText(value: Int) {
                      Text("$value", fontSize = 20.sp)
                    }

                    //2
                    fun CoroutineScope.launchCounter(): StateFlow<Int> = launchMolecule(mode = ContextClock) {
                      var count by remember { mutableStateOf(0) }

                      LaunchedEffect(Unit) {
                        while (true) {
                          delay(1_000)
                          count++
                        }
                      }

                      count
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Time

                    import kotlinx.coroutines.DelicateCoroutinesApi
                    import kotlinx.coroutines.GlobalScope
                    import kotlinx.coroutines.delay
                    import kotlinx.coroutines.flow.SharingStarted
                    import kotlinx.coroutines.flow.flow
                    import kotlinx.coroutines.flow.stateIn
                    import kotlinx.datetime.Clock
                    import kotlinx.datetime.Instant
                    import kotlinx.datetime.TimeZone
                    import kotlinx.datetime.toLocalDateTime
                    import kotlin.time.Duration.Companion.seconds

                    class TimeApi {
                        private val clockTime = flow {
                            while(true) {
                                emit(formatTime(Clock.System.now()))
                                delay(1.seconds)
                            }
                        }

                        @OptIn(DelicateCoroutinesApi::class)
                        val actualTime = clockTime.stateIn(GlobalScope, SharingStarted.Eagerly, "N/A")

                        private fun formatTime(time: Instant): String {
                            val localDateTime = time.toLocalDateTime(TimeZone.currentSystemDefault())
                            return "{localDateTime.hour}:{localDateTime.minute}:{localDateTime.second}"
                        }
                    }

                    val time = remember { TimeApi().actualTime }.collectAsState()
                    """.trimIndent()
            )

            SpanText(
                text = "Eliminate allocations per frame",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Formatting strings or creating lists inside a composable that runs frequently is a common trap. Cache derived strings with remember or compute them in a ViewModel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    val positionText by remember(state.position) { mutableStateOf("Position: ${state.position}") }
                    Text(positionText)
                    """.trimIndent()
            )

            SpanText(
                text = "Caching with remember",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The remember API is used to cache intermediate calculations and states. This avoids repeated computations, especially for operations that don’t change frequently.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    val products = remember { getProducts().sortedBy { it.priority } }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember block calculates the value during first initialization, and then caches it for later. This value will be recalculated only if one of its keys changes. If it has no keys, the same value will be used for as long as this composable exists.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    val sortedContacts = remember (contacts, comparator) {
                        contacts. sortedWith(comparator)
                    }
                    LazyColumn {
                        items (sortedContacts) {
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "@remember",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/AhmetCavus/RecompostionLab
            KotlinCode(
                code = """
                    class ScreenViewModel(
                        private val subscriptionUseCase: SubscriptionUseCase
                    ) : ViewModel() {

                        private val poorlyDesignedUiStateEmitter = MutableStateFlow<PoorlyDesignedState>(PoorlyDesignedState.Idle)
                        val poorlyDesignedUiState = poorlyDesignedUiStateEmitter.asStateFlow()

                        private val subscriptionStateEmitter = MutableStateFlow<SubscriptionState?>(null)
                        val subscriptionState = subscriptionStateEmitter.asStateFlow()

                        private val loadingStateEmitter = MutableStateFlow<Boolean>(false)
                        val loadingState = loadingStateEmitter.asStateFlow()

                        fun loadSubscriptionForPoorlyDesign() {
                            viewModelScope.launch {
                                poorlyDesignedUiStateEmitter.update { PoorlyDesignedState.Loading }
                                val isSubscribed = subscriptionUseCase.isSubscribed()
                                poorlyDesignedUiStateEmitter.update { PoorlyDesignedState.Success(isSubscribed) }
                            }
                        }

                        fun updateSubscriptionForPoorlyDesign(shouldSubscribe: Boolean) {
                            viewModelScope.launch {
                                poorlyDesignedUiStateEmitter.update { PoorlyDesignedState.Loading }
                                subscriptionUseCase.subscribeForUpdates(shouldSubscribe)
                                poorlyDesignedUiStateEmitter.update { PoorlyDesignedState.Success(shouldSubscribe) }
                            }
                        }

                        fun loadSubscription() {
                            viewModelScope.launch {
                                loadingStateEmitter.update { true }
                                val isSubscribed = subscriptionUseCase.isSubscribed()
                                subscriptionStateEmitter.update { SubscriptionState.Success(isSubscribed) }
                                loadingStateEmitter.update { false }
                            }
                        }

                        fun updateSubscription(shouldSubscribe: Boolean) {
                            viewModelScope.launch {
                                loadingStateEmitter.update { true }
                                subscriptionUseCase.subscribeForUpdates(shouldSubscribe)
                                subscriptionStateEmitter.update { SubscriptionState.Success(shouldSubscribe) }
                                loadingStateEmitter.update { false }
                            }
                        }
                    }

                    sealed interface PoorlyDesignedState {
                        data object Idle : PoorlyDesignedState
                        data object Loading : PoorlyDesignedState
                        data class Success(val isSubscribed: Boolean) : PoorlyDesignedState
                        data object Failed : PoorlyDesignedState

                        fun isLoading() = this is Loading
                    }

                    sealed interface SubscriptionState {
                        data class Success(val isSubscribed: Boolean) : SubscriptionState

                        @Immutable
                        data class Failed(val ex: Throwable) : SubscriptionState
                    }

                    @Composable
                    fun BestPerformingScreen(
                        viewModel: ScreenViewModel,
                        modifier : Modifier = Modifier,
                    ) {
                        val subscriptionState = viewModel.subscriptionState.collectAsStateWithLifecycle()
                        val loadingState = viewModel.loadingState.collectAsStateWithLifecycle()

                        val loadSubscription = remember(viewModel) { return@remember viewModel::loadSubscription }
                        val updateSubscription = remember(viewModel) { return@remember viewModel::updateSubscription }
                        var localSubscriptionState = remember { false }

                        LaunchedEffect(viewModel) {
                            loadSubscription()
                        }

                        Box(modifier = modifier) {
                            Column(modifier = Modifier.padding(16.dp)) {
                                Text("Best Performing Screen", style = MaterialTheme.typography.headlineMedium)
                                Spacer(modifier = Modifier.height(32.dp))

                                SubscriptionCheckbox(
                                    remoteSubscriptionState = subscriptionState,
                                    isLoadingState = loadingState,
                                    onCheckedChangeLocally = {
                                        localSubscriptionState = it
                                    }
                                )
                                Spacer(modifier = Modifier.height(8.dp))

                                Spacer(modifier = Modifier.weight(1f))
                                UpdateDataButton(
                                    isLoadingState = loadingState,
                                    onClick = {
                                        updateSubscription(localSubscriptionState)
                                    }
                                )
                                Spacer(modifier = Modifier.height(32.dp))
                            }
                            LoadingIndicator(
                                isVisibleState = loadingState,
                                modifier = Modifier.align(androidx.compose.ui.Alignment.Center),
                            )
                        }
                    }

                    @Composable
                    private fun SubscriptionCheckbox(
                        remoteSubscriptionState: State<SubscriptionState?>,
                        isLoadingState: State<Boolean>,
                        onCheckedChangeLocally: (Boolean) -> Unit,
                    ) {
                        Row(verticalAlignment = androidx.compose.ui.Alignment.CenterVertically) {
                            Text("Subscribe for updates", style = MaterialTheme.typography.bodyMedium)
                            Spacer(modifier = Modifier.weight(1f))

                            var shouldSubscribe by remember { mutableStateOf(false) }
                            val remoteSubscription = remoteSubscriptionState.value

                            LaunchedEffect(remoteSubscription) {
                                shouldSubscribe = if (remoteSubscription is SubscriptionState.Success) {
                                     remoteSubscription.isSubscribed
                                } else {
                                    false
                                }
                            }

                            val isLoading by isLoadingState
                            Checkbox(
                                enabled = !isLoading,
                                checked = shouldSubscribe,
                                onCheckedChange = {
                                    shouldSubscribe = it
                                    onCheckedChangeLocally(it)
                                }
                            )
                        }
                    }

                    @Composable
                    private fun LoadingIndicator(isVisibleState: State<Boolean>, modifier: Modifier = Modifier) {
                        AnimatedVisibility(
                            enter = fadeIn(),
                            exit = fadeOut(),
                            modifier = modifier,
                            visible = isVisibleState.value
                        ) {
                            CircularProgressIndicator(modifier = Modifier.height(64.dp), strokeWidth = 2.dp)
                        }
                    }

                    @Composable
                    private fun UpdateDataButton(isLoadingState: State<Boolean>, onClick: () -> Unit) {
                        Button(
                            enabled = !isLoadingState.value,
                            modifier = Modifier.fillMaxWidth(),
                            onClick = onClick
                        ) {
                            Text("Update Data")
                        }
                    }

                    @Preview(showBackground = true)
                    @Composable
                    fun PreviewBestPerformingScreen() {
                        RecompositionLabTheme {
                            PoorlyPerformingScreen(ScreenViewModel(SubscriptionUseCase(SubscriptionRepository())))
                        }
                    }

                    class MainActivity : ComponentActivity() {
                        val viewModel: ScreenViewModel = ScreenViewModel(SubscriptionUseCase(SubscriptionRepository()))

                        override fun onCreate(savedInstanceState: Bundle?) {
                            super.onCreate(savedInstanceState)
                            enableEdgeToEdge()
                            setContent {
                                RecompositionLabTheme {
                                    ScreenCoordinator(viewModel)
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Bad Way
                    @Composable
                    fun PriceCalculator(prices: List<Int>) {
                        val total = prices.sumOf { it * it }  // expensive each recomposition
                    }

                    //Good Way
                    val total by remember(prices) {
                        mutableStateOf(prices.sumOf { it * it })
                    }
                    """.trimIndent()
            )


            SpanText(
                text = "Avoid Local Mutability",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Try to avoid mutating local variables within your Composables. Instead, let state drive the UI from the top.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //Bad
                    var isVisible = true
                        if (isVisible) { /* show UI */ }

                    //Good
                    val isVisible by remember { mutableStateOf(true) }
                    """.trimIndent()
            )

            SpanText(
                text = "Why and when to use remember {}?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We can use remember composable inline function. A value calculated by remember is stored in the Composition during the initial composition, and the stored value is kept across recompositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Jetpack Compose, a composable function is a function that describes how to render a user interface element, such as a button, text view, or list.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composable functions can take input parameters and produce output elements, and they can also use other composable functions to build more complex user interface elements.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose generates a tree of elements that represents the user interface. This tree can be recomposed at any time, for example when the user interacts with the interface or when the data that the interface depends on changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you use remember{ } to create a value inside a composable function, Jetpack Compose caches the value and reuses it across recompositions, unless the value is invalidated or cleared.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember function to store and manage state, and how to use the state and mutableStateOf functions to create observable state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The remember function is used to store and manage state in Jetpack Compose. It takes an initial value and returns a mutable state object. The state and mutableStateOf functions are used to create observable state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "remember (key)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember(key) creates a new object every time a key is changed, but lambdas are usually not able to see the current value because they are bound to the first object.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "remember property",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Creates an instance of a composable that survives recompositions.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember { … }",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A lambda that is executed during the first composition and creates an object that is then remembered.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember(mutableStateOf(…))",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A helper function that creates a MutableState and returns its current value during the first composition. The state is remembered and will be updated during future recompositions.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember(key1, key2, …) { … }",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Creates a remembered value that is recomposed only when one of the specified keys changes.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "val x = remember { … }",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Declares a val that is remembered across recompositions. This can be useful for caching expensive computations or objects.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "by",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The by keyword in Kotlin doesn't directly relate to Compose. Instead, it's used to define a delegated property. In Kotlin, a delegated property represents its getter and setter operations to another object, known as the delegate.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Using by in a variable declaration, you indicate that the variable will inherit its behaviour from another object.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "remember - (Keep This Value Across Recompositions - UI-only state)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember only works on the first composition and keeps the same object, so value changes must be manually updated from the outside.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The remember function is used to remember the value across recompositions, ensuring that the state is retained even as the composable function is recomposed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It can store State object containing any primitive or custom data type.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The remember can handle both mutable and immutable objects, providing flexibility in managing state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember function to store and manage state, and how to use the state and mutableStateOf functions to create observable state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The remember function is used to store and manage state in Jetpack Compose. It takes an initial value and returns a mutable state object. The state and mutableStateOf functions are used to create observable state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun Dialog(
                        modifier: Modifier = Modifier,
                        onDashboardSubItemClick: () -> Unit = {}
                    ) {
                        var isShowingPopUp by remember {
                            mutableStateOf(false)
                        }

                        AnimatedVisibility(isShowingPopUp) {
                            BasicAlertDialog(
                                modifier = Modifier.padding(12.dp),
                                onDismissRequest = { isShowingPopUp = false },
                                properties = DialogProperties(
                                    usePlatformDefaultWidth = false
                                )
                            ) {
                                PopUp()
                            }
                        }
                    }

                    @Composable
                    private fun PopUp() { }
                    """.trimIndent()
            )

            SpanText(
                text = "How Does remember Store and Retrieve Values in the Slot Table?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember stores its computed value in the next available slot in the Slot Table, indexed by the composable's execution position — not by variable name. On recomposition, it reads from that same slot index and returns the cached value if inputs haven't changed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Remember looks at items and stores the list and query in the slot table. The filter computation then runs and remember stores the result before passing it back. The second time the function executes, remember looks at the new values being passed in and compares them with the old values. If neither of them has changed, then the filter operation is skipped, and the previous result is returned. That’s positional memoization.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The number of slots associated with a group, once created, cannot change when updating the slot table. Slots cannot be inserted, deleted, or moved; only groups can. The value in the slot can only be updated.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Simple terms, one of the most critical constraints in Jetpack Compose’s runtime is that the number and position of slots within a group must remain stable across recompositions. While Compose allows you to dynamically add or remove groups (which represent composable function calls or UI structure), it does not allow changes to the underlying slots (which store state such as values created by remember). These slots act like fixed memory positions in an internal table, and Compose relies on their exact ordering to correctly restore state during recomposition. You can update the value stored in a slot, but you cannot insert, delete, or move slots once they are created.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }
        }
    }
}